import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, Save, Tag } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'Compact', label: 'Compact (Hatchback)', color: 'bg-green-100 text-green-800' },
  { value: 'Sedan', label: 'Sedan', color: 'bg-blue-100 text-blue-800' },
  { value: 'SUV', label: 'SUV/MUV', color: 'bg-purple-100 text-purple-800' },
  { value: 'Luxury', label: 'Luxury', color: 'bg-yellow-100 text-yellow-800' }
];

const VehiclesCategoryManager = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newVehicle, setNewVehicle] = useState({ brand: '', model: '', category: '' });
  const [editFormData, setEditFormData] = useState({ brand: '', model: '', category: '' });
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/vehicles`);
      setVehicles(response.data.vehicles || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast({
        title: 'Error',
        description: 'Failed to load vehicles',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddVehicle = async () => {
    if (!newVehicle.brand || !newVehicle.model || !newVehicle.category) {
      toast({
        title: 'Validation Error',
        description: 'Please fill all fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/vehicles`, newVehicle);
      if (response.data.success) {
        toast({
          title: 'Success',
          description: 'Vehicle added successfully'
        });
        setIsAddDialogOpen(false);
        setNewVehicle({ brand: '', model: '', category: '' });
        fetchVehicles();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to add vehicle',
        variant: 'destructive'
      });
    }
  };

  const handleEditClick = (vehicle) => {
    setEditingVehicle(vehicle);
    setEditFormData({
      brand: vehicle.brand,
      model: vehicle.model,
      category: vehicle.category
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateVehicle = async () => {
    if (!editFormData.brand || !editFormData.model || !editFormData.category) {
      toast({
        title: 'Validation Error',
        description: 'Please fill all fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Update vehicle via API (we'll need to create this endpoint)
      const response = await axios.patch(
        `${BACKEND_URL}/api/vehicles/${editingVehicle.id}`,
        editFormData
      );
      
      if (response.data.success) {
        toast({
          title: 'Success',
          description: 'Vehicle updated successfully'
        });
        setIsEditDialogOpen(false);
        fetchVehicles();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update vehicle',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) {
      return;
    }

    try {
      const response = await axios.delete(`${BACKEND_URL}/api/vehicles/${vehicleId}`);
      if (response.data.success) {
        toast({
          title: 'Success',
          description: 'Vehicle deleted successfully'
        });
        fetchVehicles();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete vehicle',
        variant: 'destructive'
      });
    }
  };

  const getCategoryColor = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.color : 'bg-gray-100 text-gray-800';
  };

  // Filter vehicles by category
  const filteredVehicles = filterCategory === 'all' 
    ? vehicles 
    : vehicles.filter(v => v.category === filterCategory);

  // Group vehicles by brand
  const vehiclesByBrand = filteredVehicles.reduce((acc, vehicle) => {
    if (!acc[vehicle.brand]) {
      acc[vehicle.brand] = [];
    }
    acc[vehicle.brand].push(vehicle);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Vehicle Category Management</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Manage vehicles and their categories (Compact, Sedan, SUV, Luxury) for pricing
              </p>
            </div>
            <Button
              onClick={() => setIsAddDialogOpen(true)}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Vehicle
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Category Filter & Stats */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={filterCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterCategory('all')}
              >
                All ({vehicles.length})
              </Button>
              {categories.map(cat => (
                <Button
                  key={cat.value}
                  variant={filterCategory === cat.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterCategory(cat.value)}
                  className={filterCategory === cat.value ? cat.color : ''}
                >
                  {cat.label} ({vehicles.filter(v => v.category === cat.value).length})
                </Button>
              ))}
            </div>
          </div>
          <div className="flex gap-4 flex-wrap">
            {categories.map(cat => (
              <div key={cat.value} className={`px-4 py-2 rounded-full ${cat.color} font-medium text-sm`}>
                {cat.label}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Vehicles by Brand */}
      {Object.keys(vehiclesByBrand).sort().map(brand => (
        <Card key={brand}>
          <CardHeader>
            <CardTitle className="text-xl">{brand}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Model</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="w-[150px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vehiclesByBrand[brand].map(vehicle => (
                  <TableRow key={vehicle.id}>
                    <TableCell className="font-medium">{vehicle.model}</TableCell>
                    <TableCell>
                      <div className={`inline-block px-3 py-1 rounded-full ${getCategoryColor(vehicle.category)} text-sm font-medium`}>
                        {vehicle.category || 'Uncategorized'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditClick(vehicle)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteVehicle(vehicle.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}

      {/* Add Vehicle Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Vehicle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Brand</label>
              <Input
                value={newVehicle.brand}
                onChange={(e) => setNewVehicle({ ...newVehicle, brand: e.target.value })}
                placeholder="e.g., Maruti Suzuki"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Model</label>
              <Input
                value={newVehicle.model}
                onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })}
                placeholder="e.g., Swift"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <Select
                value={newVehicle.category}
                onValueChange={(value) => setNewVehicle({ ...newVehicle, category: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddVehicle}>
                <Plus className="w-4 h-4 mr-2" />
                Add Vehicle
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Vehicle Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Vehicle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Brand</label>
              <Input
                value={editFormData.brand}
                onChange={(e) => setEditFormData({ ...editFormData, brand: e.target.value })}
                placeholder="e.g., Maruti Suzuki"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Model</label>
              <Input
                value={editFormData.model}
                onChange={(e) => setEditFormData({ ...editFormData, model: e.target.value })}
                placeholder="e.g., Swift"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <Select
                value={editFormData.category}
                onValueChange={(value) => setEditFormData({ ...editFormData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateVehicle}>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehiclesCategoryManager;
